import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Sparkles, PhoneCall, Shield, Loader2 } from 'lucide-react';
import { usePageContent, getSectionFields } from '../hooks/usePageContent';
import FaqAccordion from '../components/FaqAccordion';
import CtaBanner from '../components/CtaBanner';

/**
 * Generic, fully admin-driven renderer for service / product pages.
 *
 * Reads the page from `/api/public-v2/pages/<slug>` and renders the four
 * standard sections — Hero, Features Grid, FAQs and CTA Banner — using
 * whatever the admin has saved in `/admin-v2/pages/<slug>`.
 *
 * Why a generic component:
 * - Every service page (Business VoIP, SIP Trunk, Microsoft Teams, etc.)
 *   shares the same overall structure: a hero, a feature grid, FAQs and a
 *   CTA. The seed populates exactly this shape per slug.
 * - One component → one bug surface → one place to evolve the layout.
 * - The admin's edits in /admin-v2/pages/<slug> show up live, no per-page
 *   custom code to maintain.
 *
 * Falls back to a "this page isn't published yet" empty state if neither
 * the page nor its sections exist — so the route never crashes.
 */
function Icon({ name, size = 22, className = '' }) {
  const Ic = Icons[name] || Sparkles;
  return <Ic size={size} className={className} />;
}

export default function ServicePageView({ slug }) {
  const { page, loading } = usePageContent(slug);

  const hero = getSectionFields(page, 'hero') || {};
  const featuresSection = getSectionFields(page, 'features_grid') || {};
  const featuresItems = Array.isArray(featuresSection.items) ? featuresSection.items : [];
  const faqsSection = getSectionFields(page, 'faqs') || {};
  const faqsItems = Array.isArray(faqsSection.items) ? faqsSection.items : [];
  const cta = getSectionFields(page, 'cta_banner') || {};

  const heroTitle = hero.title || page?.name || '';
  const heroHighlight = hero.title_highlight || '';
  const heroSubtitle = hero.subtitle || '';
  const heroBadge = hero.badge || '';
  const heroPrimaryCta = hero.primary_cta || 'Talk to a specialist';
  const heroPrimaryHref = hero.primary_href || '/contact';
  const heroImage = hero.image || '/assets/Voip-Phone-Provider.webp';

  if (loading && !page) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center text-slate-400">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading…
      </section>
    );
  }

  if (!page) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center text-slate-500">
          <p className="font-semibold">This page is not published yet.</p>
          <Link to="/" className="text-violet-600 hover:text-violet-700 text-sm mt-2 inline-block">
            ← Back to home
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden mesh-bg text-white">
        <div className="absolute inset-0 dot-pattern opacity-40" />
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] bg-fuchsia-500/15 rounded-full blur-3xl animate-pulse-slow" />

        <div className="relative container-x grid lg:grid-cols-12 items-center gap-10 lg:gap-16">
          <div className="lg:col-span-7 animate-fade-in-up">
            {heroBadge && (
              <span className="eyebrow-dark mb-6">
                <Sparkles size={12} /> {heroBadge}
              </span>
            )}
            <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              {heroTitle}
              {heroHighlight && (
                <>
                  <br />
                  <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-violet-200 bg-clip-text text-transparent">
                    {heroHighlight}
                  </span>
                </>
              )}
            </h1>
            {heroSubtitle && (
              <p className="mt-6 text-lg md:text-xl text-violet-50/85 leading-relaxed max-w-xl">
                {heroSubtitle}
              </p>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to={heroPrimaryHref} className="btn-primary justify-center">
                {heroPrimaryCta} <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative animate-float">
              <div className="absolute inset-0 -m-6 rounded-3xl bg-gradient-to-br from-violet-400/30 to-fuchsia-500/20 blur-2xl" />
              <div className="relative rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-3 shadow-[0_30px_80px_-30px_rgba(6,182,212,0.5)]">
                <img
                  src={heroImage}
                  alt={`${page.name} hero illustration`}
                  className="rounded-2xl w-full h-auto"
                  loading="eager"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="hidden md:flex absolute -left-8 top-10 items-center gap-2 bg-white text-slate-900 px-3 py-2 rounded-xl shadow-2xl">
                <span className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
                  <PhoneCall size={16} />
                </span>
                <div className="text-xs">
                  <p className="font-bold">HD Call active</p>
                  <p className="text-slate-500">Crystal clear audio</p>
                </div>
              </div>
              <div className="hidden md:flex absolute -right-6 bottom-12 items-center gap-2 bg-white text-slate-900 px-3 py-2 rounded-xl shadow-2xl">
                <span className="w-8 h-8 rounded-lg bg-fuchsia-100 text-fuchsia-600 flex items-center justify-center">
                  <Shield size={16} />
                </span>
                <div className="text-xs">
                  <p className="font-bold">Enterprise grade</p>
                  <p className="text-slate-500">SSL encrypted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      {featuresItems.length > 0 && (
        <section className="relative overflow-hidden bg-gradient-to-b from-white via-violet-50/40 to-white">
          <div className="container-x section-pad">
            <div className="max-w-3xl mx-auto text-center mb-12">
              {featuresSection.eyebrow && <span className="eyebrow">{featuresSection.eyebrow}</span>}
              <h2 className="section-title mt-4">
                {featuresSection.section_title || 'Everything you need'}
              </h2>
              {featuresSection.section_subtitle && (
                <p className="section-sub mt-3">{featuresSection.section_subtitle}</p>
              )}
              <div className="divider-svg mt-6" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuresItems.map((f, idx) => (
                <div key={idx} className="card-modern relative overflow-hidden group">
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 opacity-60 group-hover:scale-110 transition-transform" />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-lg shadow-violet-500/30">
                      <Icon name={f.icon} size={22} />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqsItems.length > 0 && (
        <FaqAccordion
          items={faqsItems}
          eyebrow={faqsSection.eyebrow || 'FAQs'}
          title={faqsSection.section_title || 'Frequently asked questions'}
          subtitle={faqsSection.section_subtitle}
        />
      )}

      {/* CTA BANNER */}
      <CtaBanner
        eyebrow={cta.eyebrow}
        title={cta.title}
        subtitle={cta.subtitle}
        primaryLabel={cta.primary_label}
        primaryHref={cta.primary_href}
        secondaryLabel={cta.secondary_label}
        secondaryHref={cta.secondary_href}
      />
    </>
  );
}
